import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveTKey } from '../../../../i18n/resolve-key';
import { Box, FormControl, IconButton, MenuItem, Select } from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';
import type { SelectChangeEvent } from '@mui/material/Select';
import type { FilterControlProps, FilterOperator } from '../filters/filter-registry.ts';
import {
  FILTER_OPERATORS,
  getOperatorsForType,
  registerFilterControl,
} from '../filters/filter-registry.ts';
import { FilterOperatorMenu } from './FilterOperatorMenu.tsx';

type BooleanFilterProps = FilterControlProps;

export function BooleanColumnFilter({ column, value, onChange, disabled }: BooleanFilterProps) {
  const { t } = useTranslation();
  const allowedOperators = useMemo(() => {
    const candidates = getOperatorsForType('boolean');
    const allowed = column.allowedFilterOperators;
    return allowed ? candidates.filter(operator => allowed.includes(operator.id)) : candidates;
  }, [column.allowedFilterOperators]);
  const defaultOperator = allowedOperators.find(operator => operator.id === 'equals')?.id
    ?? allowedOperators[0]?.id
    ?? 'equals';
  const [localOperator, setLocalOperator] = React.useState<FilterOperator>(value?.operator ?? defaultOperator);
  const [localValue, setLocalValue] = React.useState<string>(
    value?.value === true ? 'true' : value?.value === false ? 'false' : '',
  );

  useEffect(() => {
    if (value) {
      setLocalOperator(value.operator);
      setLocalValue(value.value === true ? 'true' : value.value === false ? 'false' : '');
    } else {
      setLocalOperator(defaultOperator);
      setLocalValue('');
    }
  }, [defaultOperator, value]);

  const operatorDescriptor =
    FILTER_OPERATORS.find(operator => operator.id === localOperator) ?? allowedOperators[0];
  const requiresValue = operatorDescriptor?.requiresValue !== false;

  const emit = (operator: FilterOperator, nextValue: string) => {
    const descriptor = FILTER_OPERATORS.find(candidate => candidate.id === operator);
    if (descriptor?.requiresValue === false) {
      onChange({ operator, value: null });
      return;
    }
    onChange(nextValue ? { operator, value: nextValue === 'true' } : null);
  };

  const handleOperatorChange = (operator: string) => {
    const nextOperator = operator as FilterOperator;
    setLocalOperator(nextOperator);
    emit(nextOperator, localValue);
  };

  const handleChange = (event: SelectChangeEvent<string>) => {
    const nextValue = event.target.value;
    setLocalValue(nextValue);
    emit(localOperator, nextValue);
  };

  const handleClear = () => {
    setLocalValue('');
    onChange(null);
  };

  const columnLabel = t(resolveTKey(column.labelKey));

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0 }}>
      {allowedOperators.length > 1 && (
        <FilterOperatorMenu
          column={column}
          currentOperator={localOperator}
          onChange={handleOperatorChange}
          allowedOperators={allowedOperators}
          disabled={disabled}
        />
      )}
      {requiresValue && (
        <FormControl fullWidth size="small" disabled={disabled} sx={{ minWidth: 0 }}>
          <Select
            aria-label={columnLabel}
            value={localValue}
            onChange={handleChange}
            displayEmpty
            renderValue={selected => {
              if (!selected) {
                return <Box component="span" sx={{ color: 'text.secondary' }}>{columnLabel}</Box>;
              }
              return selected === 'true'
                ? t(resolveTKey('common.yes'))
                : t(resolveTKey('common.no'));
            }}
          >
            <MenuItem value="">
              <Box component="span" sx={{ color: 'text.secondary' }}>{columnLabel}</Box>
            </MenuItem>
            <MenuItem value="true">{t(resolveTKey('common.yes'))}</MenuItem>
            <MenuItem value="false">{t(resolveTKey('common.no'))}</MenuItem>
          </Select>
        </FormControl>
      )}
      {(localValue || !requiresValue) && (
        <IconButton
          size="small"
          onClick={handleClear}
          disabled={disabled}
          aria-label={t('common:clear')}
          sx={{ flexShrink: 0 }}
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
}

function createBooleanFilter() {
  return React.memo(BooleanColumnFilter);
}

registerFilterControl('boolean', () => createBooleanFilter());
